import { useEffect, useState } from 'react';
import { MaintenanceState } from '../services/maintenanceLogic';

/**
 * Sekundentakt fuer die Anzeige der Bauphasen-Restzeit.
 *
 * useMaintenance rechnet in 'announced' bewusst nur alle 30 s nach (kein
 * 1-s-Dauerintervall im Zustand von App.tsx — jeder Tick zoege einen Render
 * der ganzen App nach sich). Wer die Restzeit ZEIGT, zaehlt deshalb selbst:
 * Anker = (Restzeit, Zeitpunkt der Lieferung); angezeigt wird
 * Restzeit − (jetzt − Lieferung). So bleibt die Server-Korrektur des Hooks
 * erhalten, und nur die Leiste bzw. die Karte rendert im Sekundentakt.
 *
 * Genau das fehlte der Leiste oben bis 10.09.2026: sie zeigte den rohen
 * Hook-Wert und „fror" fuer den Betrachter 30 s lang ein — erst ein Reload
 * brachte eine andere Zahl (Befund des Betreibers waehrend der ersten
 * Bauphase auf PROD).
 *
 * Ein neuer `state` (Hook hat nachgerechnet, Realtime, Rollenwechsel) setzt
 * den Anker neu; die Anzeige springt dann hoechstens um den Uhrenversatz,
 * den der Hook inzwischen gelernt hat.
 *
 * `nowMs` ist die Uhr des letzten Ticks (die des ersten Renders, solange
 * keiner lief) — fuer
 * Nebenanzeigen wie das Alter einer Projektsperre, nicht fuer Zeitvergleiche
 * gegen den Server.
 */
export const useMaintenanceCountdown = (state: MaintenanceState): { remainingMs: number; nowMs: number } => {
    // Kein `Date.now()` im Render und kein setState im Effektrumpf (Regeln
    // react-hooks/purity und set-state-in-effect): der Anker entsteht im
    // Effekt, gesetzt wird nur aus dem Intervall heraus. Damit die Anzeige
    // nach einem neuen `state` nicht bis zum ersten Tick den alten Stand
    // zeigt, merkt sich jeder Tick, fuer welchen `state` er gilt; solange
    // keiner passt, gilt der rohe Hook-Wert.
    const [tick, setTick] = useState<{ forState: MaintenanceState; remainingMs: number; nowMs: number } | null>(null);
    /** Uhr beim ersten Render — Rueckfall fuer `nowMs`, solange kein Tick lief. */
    const [mountedAt] = useState<number>(() => Date.now());

    useEffect(() => {
        if (state.phase === 'none') return;
        const atMs = Date.now();
        const base = state.remainingMs;
        const timer = window.setInterval(() => {
            const now = Date.now();
            setTick({ forState: state, remainingMs: Math.max(0, base - (now - atMs)), nowMs: now });
        }, 1_000);
        return () => window.clearInterval(timer);
    }, [state]);

    const current = tick !== null && tick.forState === state ? tick : null;
    return {
        remainingMs: current ? current.remainingMs : state.remainingMs,
        nowMs: current ? current.nowMs : (tick ? tick.nowMs : mountedAt),
    };
};
